import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Radio, Newspaper, Info, MessageSquare, Trees, Briefcase } from "lucide-react";
import { FONT, SAFE_BOTTOM, GLASS, glassBox, glassInner } from "./ui";

// Slot dock (susunan ala Thaloria, bahasa kaca Jawsel): ikon + label (+ label pendek untuk HP sangat sempit)
export const DOCK_ITEMS = [
  ["radio", Radio, "Radio"],
  ["koran", Newspaper, "Koran"],
  ["pesan", MessageSquare, "Pesan Warga", "Pesan"],
  ["alun", Trees, "Alun-alun"],
  ["kerja", Briefcase, "Kerja"],
  ["info", Info, "Info"],
];

// true bila layar ≤ 349 px (mis. iPhone SE) — label panjang diganti versi pendek agar tidak keluar kotak
function useLayarSempit() {
  const [sempit, setSempit] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 349px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 349px)");
    const h = e => setSempit(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return sempit;
}

// Dock bawah kaca: panel tembus pandang menempel di dasar layar — baris ikon berlabel, lalu baris status
// (jumlah bangunan/jalan) + tombol "Cari". Radio = saklar on/off (pemutarnya tampil sebagai pil kaca di kiri-atas).
// `badge` = { pesan: true } → titik biru kecil di ikon (ada yang baru sejak kunjungan terakhir).
export function BottomDock({ active, onOpen, radio, badge = {} }) {
  const sempit = useLayarSempit();
  return (
    <div data-testid="kota-bottomdock" style={{ position: "absolute", left: 0, right: 0, bottom: 0, display: "flex", justifyContent: "center", fontFamily: FONT, pointerEvents: "none" }}>
      <style>{`@keyframes jw-eq{0%,100%{transform:scaleY(.35)}50%{transform:scaleY(1)}}`}</style>
      <div style={{ pointerEvents: "auto", width: "100%", maxWidth: 560, ...glassBox, borderBottom: 0, borderRadius: "28px 28px 0 0",
        padding: `10px 10px calc(${SAFE_BOTTOM} + 8px)`, boxShadow: GLASS.shadow }}>
        <nav data-testid="dock-items" style={{ display: "grid", gridTemplateColumns: `repeat(${DOCK_ITEMS.length}, 1fr)`, gap: 2 }}>
          {DOCK_ITEMS.map(([key, Icon, label, pendek]) => {
            const teks = sempit && pendek ? pendek : label;
            const isRadio = key === "radio";
            const on = isRadio ? radio.playing : active === key;
            const dot = (isRadio && radio.playing) || badge[key];
            return (
              <button key={key} data-testid={`dock-${key}`} aria-label={label} aria-pressed={on}
                onClick={() => {
                  if (!isRadio) return onOpen(active === key ? null : key);
                  if (radio.loaded && !radio.ready) return toast("Playlist radio masih kosong — admin belum mengunggah lagu");
                  radio.toggle();
                }}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, borderRadius: 18, cursor: "pointer", padding: "9px 2px 7px", minWidth: 0,
                  ...glassInner(on), color: on ? GLASS.accent : GLASS.ink, boxShadow: on ? "0 4px 14px rgba(20,20,20,0.06)" : "none",
                  transition: "background-color .3s, color .3s, transform .12s, box-shadow .18s", WebkitTapHighlightColor: "transparent", fontFamily: FONT }}
                onPointerDown={e => { e.currentTarget.style.transform = "scale(.95)"; }}
                onPointerUp={e => { e.currentTarget.style.transform = "scale(1)"; }}
                onPointerLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
                <span style={{ position: "relative", width: 26, height: 24, display: "grid", placeItems: "center" }}>
                  {isRadio && radio.playing ? <Equalizer /> : <Icon size={22} strokeWidth={1.8} />}
                  {dot && <span data-testid={isRadio ? "radio-live-dot" : `dock-badge-${key}`} style={{ position: "absolute", top: -2, right: -4, width: 7, height: 7, borderRadius: 99, background: GLASS.accent, boxShadow: "0 0 0 2px var(--g-bg-strong)" }} />}
                </span>
                {/* Label panjang (≥10 huruf, mis. "Pesan Warga") dikecilkan agar selalu muat satu baris di kotaknya */}
                <span style={{ fontSize: label.length >= 10 ? 8.5 : 10, fontWeight: on ? 700 : 600, letterSpacing: label.length >= 10 ? -0.3 : 0, whiteSpace: "nowrap", maxWidth: "100%", overflow: "hidden", color: on ? GLASS.accent : GLASS.ink }}>{teks}</span>
              </button>
            );
          })}
        </nav>

        {/* Baris cari: ketuk di mana saja → sheet Cari (nama warga / bangunan) */}
        <div data-testid="dock-statusbar" role="button" onClick={() => onOpen(active === "cari" ? null : "cari")}
          style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, height: 44, padding: "0 5px 0 14px", ...glassInner(false), border: "1px solid var(--g-border)", borderRadius: 99, cursor: "pointer" }}>
          <Search size={15} strokeWidth={2} style={{ flex: "none", color: GLASS.sub }} />
          <span data-testid="kota-status" style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: GLASS.sub, letterSpacing: -0.1 }}>
            Cari nama warga
          </span>
          <button data-testid="dock-cari" onClick={e => { e.stopPropagation(); onOpen(active === "cari" ? null : "cari"); }}
            style={{ height: 34, padding: "0 16px", borderRadius: 99, ...glassInner(true), color: GLASS.accent, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT, flex: "none", boxShadow: "0 4px 12px rgba(20,20,20,0.06)", letterSpacing: -0.1 }}>
            Cari
          </button>
        </div>
        <div style={{ width: 110, height: 4, borderRadius: 99, background: GLASS.handle, margin: "10px auto 0" }} />
      </div>
    </div>
  );
}

function Equalizer() {
  return (
    <span data-testid="radio-equalizer" style={{ display: "inline-flex", alignItems: "flex-end", gap: 3, height: 20 }}>
      {[0, .18, .36, .12].map((d, i) => (
        <span key={i} style={{ width: 3.5, height: 18, borderRadius: 2, background: "currentColor", transformOrigin: "bottom", animation: `jw-eq ${.75 + i * .1}s ${d}s ease-in-out infinite` }} />
      ))}
    </span>
  );
}
